import React, { useState } from 'react';
import { Box, Typography, Button, IconButton, Slider, CircularProgress } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import FlagIcon from '@mui/icons-material/Flag';
import { API } from 'aws-amplify';
import { updateVideoData } from '../../graphql/mutations';

function formatTime(secs) {
  if (secs == null || isNaN(secs) || secs < 0) return '--:--.-';
  const m = Math.floor(secs / 60);
  const s = (secs % 60).toFixed(1).padStart(4, '0');
  return `${m}:${s}`;
}

/**
 * Props:
 *   videoData    - full VideoData object
 *   currentTime  - live current time in seconds (polled by parent)
 *   duration     - video duration in seconds
 *   isPlaying    - solo play state
 *   onPlay / onPause / onScrub(seconds) / onSave(updatedVideoData)
 */
function GreenFlagWidget({ videoData, currentTime, duration = 0, isPlaying, onPlay, onPause, onScrub, onSave }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  // Local drag value — tracks slider position visually while dragging
  const [dragValue, setDragValue] = useState(null);

  const handleSet = async () => {
    if (currentTime == null || isNaN(currentTime) || currentTime < 0) return;
    onPause?.();
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const result = await API.graphql({
        query: updateVideoData,
        variables: {
          input: {
            id: videoData.id,
            greenFlag: parseFloat(currentTime.toFixed(2)),
            _version: videoData._version,
          },
        },
      });
      setSaved(true);
      onSave?.(result.data.updateVideoData);
    } catch (err) {
      console.error('GreenFlagWidget save error:', err);
      setError('Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const hasTime = currentTime != null && !isNaN(currentTime) && currentTime >= 0;

  return (
    <Box
      sx={{
        bgcolor: 'rgba(0,0,0,0.8)',
        borderTop: '1px solid rgba(255,255,255,0.08)',
        px: 1.5,
        pt: 0.75,
        pb: 0.5,
      }}
    >
      {/* Controls row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton
          size="small"
          onClick={isPlaying ? onPause : onPlay}
          sx={{ color: 'white', p: 0.5 }}
        >
          {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
        </IconButton>

        <FlagIcon sx={{ fontSize: 13, color: 'success.main', flexShrink: 0 }} />

        <Typography
          variant="caption"
          fontFamily="monospace"
          color={hasTime ? 'white' : 'grey.600'}
          sx={{ minWidth: 56 }}
        >
          {formatTime(currentTime)}
        </Typography>

        <Button
          size="small"
          variant="outlined"
          color="success"
          onClick={handleSet}
          disabled={saving || !hasTime || saved}
          sx={{
            ml: 'auto',
            minWidth: 0,
            px: 1,
            py: 0.25,
            fontSize: 11,
            lineHeight: 1.4,
            borderColor: 'success.dark',
            color: saved ? 'success.main' : 'success.light',
          }}
        >
          {saving ? <CircularProgress size={12} /> : saved ? 'Saved' : 'Set'}
        </Button>

        {error && (
          <Typography variant="caption" color="error">
            {error}
          </Typography>
        )}
      </Box>

      {/* Mini scrubber */}
      <Slider
        value={dragValue ?? (hasTime ? currentTime : 0)}
        min={0}
        max={duration || 100}
        step={0.1}
        onChange={(_, val) => {
          // Update display immediately while dragging, don't seek yet
          setDragValue(val);
        }}
        onChangeCommitted={(_, val) => {
          // Seek the video and update parent on release
          setDragValue(null);
          onScrub?.(val);
        }}
        disabled={!duration}
        size="small"
        sx={{
          py: 0.5,
          color: 'success.main',
          '& .MuiSlider-thumb': { width: 10, height: 10 },
          '& .MuiSlider-rail': { opacity: 0.3 },
        }}
      />
    </Box>
  );
}

export default GreenFlagWidget;
